import React from 'react';
import { 
  DollarSign, Clock, Shield, MessageCircle, FileText, Search, Target, 
  CheckCircle, Handshake, Star, ChevronDown, ChevronUp, PieChart, CreditCard,
  Database, Briefcase, Users, TrendingUp, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import ConsultationForm from './ConsultationForm';
import { AnimatedSection, StaggeredContainer } from './AnimatedSection';
import { 
  trustedLogos, approachSteps, features, howItWorksSteps, 
  testimonials, faqData, stats, trustBadges, howWeWorkColumns 
} from '../mock/data';

const iconMap = {
  DollarSign, Clock, Shield, MessageCircle, FileText, Search, Target, CheckCircle, PieChart, CreditCard, Database, Briefcase, Users, TrendingUp, RefreshCw
};

const HomePage = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="home" className="min-h-[90vh] flex flex-col lg:flex-row">
        {/* Left Column - Black Background */}
        <div className="w-full lg:w-1/2 bg-black flex items-center justify-center px-8 lg:px-12 py-12 lg:py-0">
          <AnimatedSection animation="slideInLeft" className="text-white max-w-lg text-center lg:text-left">
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6 font-clash">
              Funds recovery service. <span className="text-white">Reclaim what's yours!</span>
            </h1>
            <p className="text-lg text-white leading-relaxed font-medium">
              ChargebackCanada helps online scam victims recover their losses. We use proven dispute resolution and investigative methods to track down stolen funds. If you've been defrauded, contact us - we're here to help.
            </p>
            <p className="text-lg leading-relaxed font-medium mt-4" style={{ color: '#FF3D00' }}>
              If your loss is less than $2,000, you are not eligible for this service.
            </p>
          </AnimatedSection>
        </div>

        {/* Right Column - Banknote Background with Form */}
        <div 
          className="w-full lg:w-1/2 relative flex items-center justify-center px-8 py-12 lg:py-0"
          style={{
            backgroundImage: `url('https://agents.beunframed.com/wp-content/uploads/2025/08/photo-1515606378517-3451a4fa2e12-scaled.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Overlay for better contrast */}
          <div className="absolute inset-0 bg-black bg-opacity-20"></div>
          
          {/* Form Card */}
          <AnimatedSection animation="slideInRight" delay={300} className="relative z-10 w-full max-w-md lg:mx-auto">
            <ConsultationForm 
              title="Get in touch with us"
              buttonText="Get free consultation"
              isHeroForm={true}
            />
          </AnimatedSection>
        </div>
      </section>

      {/* Trusted Logos Section */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-8 lg:px-8 max-w-6xl">
          <AnimatedSection animation="fadeInUp" className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-black mb-6 font-clash">
              Businesses we have successfully recovered funds from
            </h2>
            <p className="text-lg text-[#333333] leading-relaxed">
              If you're interested in partnering with us or becoming a sponsor, feel free to reach out.
            </p>
          </AnimatedSection>
          
          {/* Logo Grid - 5 columns on desktop, responsive on mobile */}
          <StaggeredContainer staggerDelay={100} className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12 items-center justify-items-center">
            {trustedLogos.map((logo, index) => (
              <div key={index} className="flex items-center justify-center p-4">
                <img
                  src={logo.url}
                  alt={logo.name}
                  className="max-w-full h-auto max-h-16 object-contain transition-all duration-300 hover:scale-105"
                />
              </div>
            ))}
          </StaggeredContainer>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 lg:py-24" style={{ backgroundColor: '#111111' }}>
        <div className="container mx-auto px-8 lg:px-8 max-w-6xl">
          <AnimatedSection animation="fadeInUp" className="text-left mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 font-clash">Our approach</h2>
          </AnimatedSection>
          <StaggeredContainer staggerDelay={150} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {approachSteps.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              return (
                <div key={index} className="text-left">
                  <div className="mb-6 flex justify-start">
                    <IconComponent 
                      className="text-6xl lg:text-7xl transition-all duration-300 hover:scale-110" 
                      style={{ color: '#FF3D00' }}
                      size={64}
                    />
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-white mb-4 font-clash">{step.title}</h3>
                  <p className="text-gray-300 text-sm lg:text-base leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </StaggeredContainer>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section id="why-us" className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-8 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Content */}
            <div className="lg:max-w-lg">
              <h2 className="text-2xl lg:text-3xl font-bold text-black mb-6 font-clash">
                Why work with us?
              </h2>
              <p className="text-lg text-[#111111] leading-relaxed mb-8 max-w-md">
                Our dedicated team works daily to recover refunds for businesses and individuals affected by scams. We are committed to improving our services and providing unwavering support to help you obtain the compensation you deserve.
              </p>
              <div className="mb-3">
                <div className="text-4xl lg:text-5xl font-extrabold text-black font-clash">
                  $16,432,423+
                </div>
              </div>
              <div className="text-lg text-[#222222]">
                Money retrieved in July 2025
              </div>
            </div>
            
            {/* Right Column - Empty for spacing */}
            <div className="hidden lg:block">
              {/* Intentionally empty for spacing as specified */}
            </div>
          </div>
        </div>
      </section>

      {/* Get Your Money Back CTA Section */}
      <section className="w-full">
        <div className="flex flex-col lg:flex-row min-h-[500px] lg:min-h-[600px]">
          {/* Left Column - Handshake Image */}
          <div className="w-full lg:w-1/2 min-h-[300px] lg:min-h-full relative">
            <img
              src="https://agents.beunframed.com/wp-content/uploads/2025/08/radission-us-_XeQ8XEWb4Q-unsplash-scaled.webp"
              alt="Professional handshake with Lady Justice statue"
              className="absolute inset-0 w-full h-full object-cover filter grayscale"
            />
          </div>
          
          {/* Right Column - Black Background with CTA */}
          <div className="w-full lg:w-1/2 bg-black flex items-center justify-center p-12 min-h-[400px] lg:min-h-full">
            <div className="text-center lg:text-left w-full max-w-md mx-auto lg:mx-0">
              <h2 className="text-2xl sm:text-3xl lg:text-3xl font-extrabold text-white mb-6 font-clash leading-tight">
                Let's get your<br className="hidden sm:block" />
                <span className="sm:hidden"> </span>money back!
              </h2>
              <p className="text-base sm:text-lg text-white mb-8 leading-relaxed max-w-sm mx-auto lg:mx-0">
                If you've lost money to scammers, contact us now, and we'll work with you to recover your funds!
              </p>
              <button 
                onClick={() => scrollToSection('#home')}
                className="w-full sm:w-auto text-white font-bold px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                style={{ backgroundColor: '#FF3D00' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#E53500'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#FF3D00'}
              >
                Get free consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Features Section */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-8 lg:px-8 max-w-6xl">
          <AnimatedSection animation="fadeInUp" className="mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-black font-clash text-left">Our features</h2>
          </AnimatedSection>
          <StaggeredContainer staggerDelay={120} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={index} className="text-left group">
                  <div className="mb-4">
                    <IconComponent 
                      className="text-4xl lg:text-5xl transition-all duration-300 group-hover:scale-110" 
                      style={{ color: '#FF3D00' }}
                      size={48}
                    />
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-black mb-3 font-clash">{feature.title}</h3>
                  <p className="text-sm lg:text-base text-black leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </StaggeredContainer>
        </div>
      </section>

      {/* Gray Divider */}
      <div className="w-full">
        <div className="container mx-auto px-8 lg:px-8 max-w-6xl">
          <hr className="border-gray-300 border-t-1" />
        </div>
      </div>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-8 lg:px-8 max-w-6xl">
          <div className="mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-black font-clash text-left">How it works?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="text-left">
                <div className="flex items-start mb-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0"
                    style={{ backgroundColor: '#FF3D00' }}
                  >
                    {step.number}
                  </div>
                  <div>
                    <h3 className="text-lg lg:text-xl font-bold text-black mb-3 font-clash">{step.title}</h3>
                  </div>
                </div>
                <p className="text-sm lg:text-base text-black leading-relaxed ml-16">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Successes Section */}
      <section className="w-full">
        <div className="flex flex-col lg:flex-row min-h-[600px]">
          {/* Left Column - Black Background with Stats */}
          <div className="w-full lg:w-1/2 bg-black flex items-center justify-center px-8 lg:px-12 py-12 lg:py-0 min-h-[500px] lg:min-h-full">
            <div className="text-left w-full max-w-lg">
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-12 font-clash">
                Our successes
              </h2>
              
              {/* Stats List */}
              <div className="space-y-8 mb-12">
                {/* Success Rate Stat */}
                <div className="flex items-start">
                  <TrendingUp 
                    className="mr-4 mt-1 flex-shrink-0" 
                    style={{ color: '#FF3D00' }}
                    size={32}
                  />
                  <div>
                    <div className="text-3xl lg:text-4xl font-bold text-white mb-1 font-clash">
                      {stats.successRate.value}
                    </div>
                    <div className="text-gray-300 text-sm lg:text-base">
                      {stats.successRate.label}
                    </div>
                  </div>
                </div>
                
                {/* Cases Won Stat */}
                <div className="flex items-start">
                  <Briefcase 
                    className="mr-4 mt-1 flex-shrink-0" 
                    style={{ color: '#FF3D00' }}
                    size={32}
                  />
                  <div>
                    <div className="text-3xl lg:text-4xl font-bold text-white mb-1 font-clash">
                      {stats.casesWon.value}
                    </div>
                    <div className="text-gray-300 text-sm lg:text-base">
                      {stats.casesWon.label}
                    </div>
                  </div>
                </div>
                
                {/* Funds Retrieved Stat */}
                <div className="flex items-start">
                  <RefreshCw 
                    className="mr-4 mt-1 flex-shrink-0" 
                    style={{ color: '#FF3D00' }}
                    size={32}
                  />
                  <div>
                    <div className="text-3xl lg:text-4xl font-bold text-white mb-1 font-clash">
                      {stats.fundsRetrieved.value}
                    </div>
                    <div className="text-gray-300 text-sm lg:text-base">
                      {stats.fundsRetrieved.label}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* CTA Button */}
              <button 
                onClick={() => scrollToSection('#home')}
                className="w-full sm:w-auto text-white font-bold px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                style={{ backgroundColor: '#FF3D00' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#E53500'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#FF3D00'}
              >
                Get free consultation
              </button>
            </div>
          </div>
          
          {/* Right Column - Grayscale Business Image */}
          <div className="w-full lg:w-1/2 min-h-[400px] lg:min-h-full relative">
            <img
              src="https://agents.beunframed.com/wp-content/uploads/2025/08/image-28-scaled.webp"
              alt="Business analytics with laptop and mobile devices"
              className="absolute inset-0 w-full h-full object-cover filter grayscale"
            />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-8 lg:px-8 max-w-6xl">
          <AnimatedSection animation="fadeInUp" className="text-left mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-black mb-6 font-clash">Customer testimonials</h2>
            <p className="text-lg text-black leading-relaxed max-w-2xl">
              At ChargebackCanada, we value customer feedback and continually strive to enhance our services to exceed expectations.
            </p>
          </AnimatedSection>
          
          {/* Testimonials Grid - 3 rows, 2 columns */}
          <StaggeredContainer staggerDelay={200} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="relative group">
                {/* Speech Bubble Card */}
                <div className="bg-[#F8F8F8] rounded-2xl p-6 lg:p-8 relative transition-all duration-300 group-hover:shadow-lg group-hover:bg-gray-50">
                  {/* Speech Bubble Tail */}
                  <div className="absolute bottom-[-8px] left-6 w-4 h-4 bg-[#F8F8F8] transform rotate-45 transition-colors duration-300 group-hover:bg-gray-50"></div>
                  
                  {/* Quote Text */}
                  <p className="text-black text-sm lg:text-base leading-relaxed mb-6">
                    "{testimonial.quote}"
                  </p>
                </div>
                
                {/* Customer Info - Outside bubble, aligned to bottom left */}
                <div className="flex items-center mt-4 ml-2">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full mr-3 overflow-hidden transition-transform duration-300 group-hover:scale-105">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-black font-bold text-sm lg:text-base">
                    {testimonial.name}
                  </div>
                </div>
              </div>
            ))}
          </StaggeredContainer>
          
          {/* Trust Badges */}
          <AnimatedSection animation="fadeInUp" delay={600} className="border-t border-gray-200 pt-12">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12 items-center justify-items-center">
              {trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center justify-center">
                  <img
                    src={badge.url}
                    alt={badge.name}
                    className="h-8 lg:h-10 opacity-60 hover:opacity-80 transition-all duration-300 hover:scale-105 max-w-full object-contain"
                  />
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Prospective Relationships Section */}
      <section className="w-full">
        <div className="flex flex-col lg:flex-row min-h-[600px] lg:min-h-screen">
          {/* Left Column - Black Background with Content */}
          <div className="w-full lg:w-1/2 bg-black flex items-center justify-center px-8 lg:px-12 py-12 lg:py-0 min-h-[500px] lg:min-h-full">
            <div className="text-left w-full max-w-lg">
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-8 font-clash leading-tight">
                Prospective<br />relationships
              </h2>
              <p className="text-lg lg:text-xl text-white mb-12 leading-relaxed max-w-md">
                We focus on cultivating relationships that prioritize long-term partnerships, fostering trust and paving the way for future success.
              </p>
              <button 
                onClick={() => scrollToSection('#home')}
                className="text-white font-bold px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                style={{ backgroundColor: '#FF3D00' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#E53500'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#FF3D00'}
              >
                Contact Us
              </button>
            </div>
          </div>
          
          {/* Right Column - Lady Justice Image */}
          <div className="w-full lg:w-1/2 min-h-[400px] lg:min-h-full relative">
            <img
              src="https://agents.beunframed.com/wp-content/uploads/2025/08/image-29-scaled.webp"
              alt="Lady Justice statue with balance scales and legal documents on desk"
              className="absolute inset-0 w-full h-full object-cover filter grayscale"
            />
          </div>
        </div>
      </section>

      {/* How We Work Section + FAQ Combined */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-8 lg:px-8 max-w-6xl">
          {/* How We Work to Recover Your Money */}
          <div className="mb-20">
            <div className="mb-16">
              <h2 className="text-2xl lg:text-3xl font-bold text-black font-clash text-left leading-tight">
                How we work to recover<br />your money
              </h2>
            </div>
            
            {/* 3-Column Description Layout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 mb-16">
              {howWeWorkColumns.map((column, index) => (
                <div key={index} className="text-left">
                  <p className="text-base lg:text-lg text-black leading-relaxed">
                    {column.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div id="faq">
            <AnimatedSection animation="fadeInUp" delay={300} className="w-full">
              <div className="mb-16">
                <h2 className="text-2xl lg:text-3xl font-bold text-black font-clash text-left">FAQ</h2>
              </div>
              <Accordion type="single" collapsible className="space-y-6">
                {faqData.map((faq, index) => (
                  <AnimatedSection key={index} animation="fadeInUp" delay={400 + (index * 100)}>
                    <AccordionItem value={`item-${index}`} className="border-b border-gray-200 pb-6 group">
                      <AccordionTrigger className="text-left font-bold text-lg text-black hover:text-gray-700 transition-colors duration-200 [&>svg]:w-5 [&>svg]:h-5 [&>svg]:text-gray-500">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-black leading-relaxed pt-4 text-base">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </AnimatedSection>
                ))}
              </Accordion>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* How We Work to Get Our Clients Money Back Section */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-8 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Content */}
            <div className="lg:max-w-lg">
              <h2 className="text-2xl lg:text-3xl font-bold text-black mb-6 font-clash">
                How we work to get our clients money back
              </h2>
              <div className="text-lg text-[#111111] leading-relaxed space-y-4">
                <p>PLEASE NOTE THAT WE ARE NOT A GOVERNMENT AGENCY.</p>
                <p>WE ARE A NON-GOVERNMENT ENTITY PROVIDING VALUE ADDED PAID SERVICES FOR ADDITIONAL ASSISTANCE.</p>
                <p>WE DON'T OFFER ANY INVESTMENTS, FINANCIAL SERVICES, OR ADVICE.</p>
                <p>WE DON'T ACCEPT UNITED STATES CITIZENS AS CUSTOMERS</p>
                <p>IF YOU ARE AMERICAN AND GOT SCAMMED PLEASE REPORT TO fbi.gov</p>
              </div>
            </div>
            
            {/* Right Column - Empty for spacing */}
            <div className="hidden lg:block">
              {/* Intentionally empty for spacing as specified */}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="container mx-auto px-8 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
            <div className="mb-4 md:mb-0">
              <h3 className="font-bold text-xl font-clash">ChargebackCanada</h3>
              <p className="text-gray-400 text-sm">Helping you reclaim what's rightfully yours</p>
            </div>
            <div className="text-gray-400 text-sm">
              © 2025 ChargebackCanada. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;